import { useCallback, useEffect, useMemo, useState } from "react";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { useAppDispatch } from "store/hooks";
import { getCoverageAreaDealerId } from "store/modules/Logistics/logisticsCoverageArea.helpers";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { handleSaveMapAreaDriversRequest } from "store/modules/Logistics/thunk.store";
import { patchCoverageAreaDrivers } from "store/modules/Logistics/index.store";
import { ECoverageAreaType } from "enums/options/coverageArea";
import {
  buildSingleDriverStatePatch,
  clearSingleDriverStatePatch,
} from "components/CardDriverArea/buildCoverageAreaDriversStatePatch";
import { getOrderedCoverageAreaDrivers } from "components/CardDriverArea/coverageAreaDrivers";

const COVERAGE_AREA_TYPE_REQUIRES_DRIVER = ECoverageAreaType.DriverStore;

function resolveSelectedDriverLabel(
  primaryDriver: { id: string; name?: string; surname?: string | null },
  dealerDrivers: DealerUsersProps[],
): { label: string; id: string } {
  const user = dealerDrivers.find((driver) => driver.id === primaryDriver.id);

  if (user?.name) {
    return {
      label: `${user.name ?? ""} ${user.surname ?? ""}`.trim(),
      id: user.id,
    };
  }

  const label = [primaryDriver.name, primaryDriver.surname]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    label,
    id: primaryDriver.id,
  };
}

export function useCardDriverAreaSingle(
  dealerSelected: string,
  currentCoverageArea: CoverageAreaProps,
  dealerDrivers: DealerUsersProps[],
) {
  const dispatch = useAppDispatch();
  const [allIUsers, setAllIUsers] = useState<DealerUsersProps[]>(dealerDrivers);
  const [addDrive, setAddDrive] = useState(false);
  const [driveSelected, setDriveSelected] = useState<{
    label: string;
    id: string;
  }>();

  useEffect(() => {
    setAddDrive(false);
  }, [dealerSelected, currentCoverageArea.id]);

  useEffect(() => {
    setAllIUsers(dealerDrivers);
  }, [dealerDrivers]);

  const IUsers = useMemo(
    () =>
      allIUsers.map((user) => ({
        label: `${user?.name ?? ""} ${user?.surname ?? ""}`.trim(),
        id: user?.id,
      })),
    [allIUsers],
  );

  const orderedCoverageDrivers = useMemo(
    () => getOrderedCoverageAreaDrivers(currentCoverageArea),
    [currentCoverageArea],
  );

  const coverageAreaId = currentCoverageArea.id;

  useEffect(() => {
    const primaryDriver = orderedCoverageDrivers[0]?.details;
    if (!primaryDriver?.id) {
      setDriveSelected(undefined);
      return;
    }

    setDriveSelected(resolveSelectedDriverLabel(primaryDriver, dealerDrivers));
  }, [coverageAreaId, dealerDrivers, orderedCoverageDrivers]);

  const selectedDriverWithDealerInfo = useMemo(() => {
    const primaryId = orderedCoverageDrivers[0]?.details.id;
    return IUsers.find((user) => user.id === primaryId);
  }, [IUsers, orderedCoverageDrivers]);

  const handleAddDrive = useCallback(async () => {
    if (!addDrive || !driveSelected?.id) return;
    try {
      await dispatch(
        handleSaveMapAreaDriversRequest({
          dealerId: getCoverageAreaDealerId(currentCoverageArea),
          coverageAreaId: currentCoverageArea.id,
          selectedDriverIdsInOrder: [driveSelected.id],
        }),
      ).unwrap();
      dispatch(
        patchCoverageAreaDrivers({
          coverageAreaId: currentCoverageArea.id,
          patch: buildSingleDriverStatePatch(
            { id: driveSelected.id, label: driveSelected.label },
            getCoverageAreaDealerId(currentCoverageArea),
            allIUsers,
          ),
        }),
      );
      setAddDrive(false);
    } catch {
      //
    }
  }, [addDrive, allIUsers, currentCoverageArea, dispatch, driveSelected]);

  const handleRemoveDrive = useCallback(async () => {
    if (!addDrive) return;
    try {
      await dispatch(
        handleSaveMapAreaDriversRequest({
          dealerId: getCoverageAreaDealerId(currentCoverageArea),
          coverageAreaId: currentCoverageArea.id,
          selectedDriverIdsInOrder: [],
        }),
      ).unwrap();
      dispatch(
        patchCoverageAreaDrivers({
          coverageAreaId: currentCoverageArea.id,
          patch: clearSingleDriverStatePatch(),
        }),
      );
      setAddDrive(false);
    } catch {
      //
    }
  }, [addDrive, currentCoverageArea, dispatch]);

  const hasAreaDriverAssigned = orderedCoverageDrivers.length > 0;

  const isApplyDisabled =
    currentCoverageArea.type === COVERAGE_AREA_TYPE_REQUIRES_DRIVER &&
    !driveSelected?.label?.length;

  const handleApply = useCallback(() => {
    if (driveSelected?.label?.length) {
      return handleAddDrive();
    }
    return handleRemoveDrive();
  }, [driveSelected?.label, handleAddDrive, handleRemoveDrive]);

  const openDriverSelection = useCallback(() => {
    setAddDrive(true);
  }, []);

  return {
    addDrive,
    setAddDrive,
    driveSelected,
    setDriveSelected,
    IUsers,
    openDriverSelection,
    handleApply,
    isApplyDisabled,
    selectedDriverWithDealerInfo,
    hasAreaDriverAssigned,
  };
}
